/**
 * QR-2 В2: жизненный цикл лидерства msg-машины для СТРАНИЦЫ.
 *
 * 🔴 Почему крючок, а не копия. Замок `total-msg-leader` один на origin, и
 * протокол вокруг него у всех страниц ОДИН И ТОТ ЖЕ: дождаться эстафеты этой
 * вкладки -> попросить лок -> поднять контроллер -> на уходе остановить его и
 * только ПОТОМ отпустить лок, положив это ожидание в эстафету. Доменного
 * ветвления здесь нет вовсе — ни слова «визави», ни слова «комната», — поэтому
 * общий код тут не тот случай, от которого предостерегает §1.1 дизайна
 * (там речь про КОНТРОЛЛЕР, где почти всё специфично). А вот копипаста этих
 * ста строк в третью страницу — надёжный способ уронить чат на бою: каждая
 * тонкость ниже добыта отдельным раундом ревью.
 *
 * Что здесь удержано (и почему это не мелочи):
 * - ожидание эстафеты ДО запроса лока (QR-1b р.3): иначе новый mount получает
 *   отказ от СОБСТВЕННОГО, ещё не отпущенного лока — «открыто в другой
 *   вкладке» навсегда;
 * - спиннер переноса гасится на ОБОИХ путях, включая отменённый (с.64 р.1
 *   High-1): булев флаг оставлял «Переносим…» навечно;
 * - номер попытки, а не флаг: заход гасит спиннер, только если тот его;
 * - `heldRef` — уступает ТОЛЬКО держатель: заблокированная вкладка на чужую
 *   просьбу не отвечает;
 * - висящий запрос лидерства на уходе (р.4): лок выдадут «мёртвому» заходу,
 *   поэтому эстафета = дождаться выдачи и отпустить;
 * - `stop()` перед `release()` (QR-1b р.2): две машины на одном IndexedDB-сторе
 *   портят Olm-состояние.
 *
 * Страница `msgChat` пока живёт на своей копии этого протокола: она на бою с
 * 25.08, и её переезд сюда — отдельная работа с отдельным ревью.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  askYield, onYieldRequest, requestLeadership, setLocalHandoff, TAKEOVER_WAIT_MS,
  waitLocalHandoff,
} from './leader';

/**
 * @param enabled  поднимать ли машину вовсе (гейты страницы уже посчитаны)
 * @param startController  () => контроллер с методами start() и stop().
 *        Вызывается ТОЛЬКО когда лок получен; должен сам запустить подъём.
 * @param deps  что перезапускает подъём (смена комнаты/визави и т.п.)
 */
export function useMsgLeadership({ enabled, startController, deps = [] }) {
  const [blocked, setBlocked] = useState(false);
  const [yielded, setYielded] = useState(false);
  const [takeFailed, setTakeFailed] = useState(false);
  const [takeTick, setTakeTick] = useState(0);
  // 🔴 Повтор подъёма — ОТДЕЛЬНЫЙ счётчик, а не тот же, что перенос: иначе
  // обычное «проверить сейчас» уходило бы в ветку переноса (ожидание лока в
  // очереди на TAKEOVER_WAIT_MS и «сосед не ответил» в отказе).
  const [retryTick, setRetryTick] = useState(0);
  // 🔴 Номер попытки, а не булев флаг (с.64, High-1).
  const [takingTick, setTakingTick] = useState(0);
  const takingTickRef = useRef(0);
  const heldRef = useRef(false);
  const controllerRef = useRef(null);
  const taking = takingTick > 0;

  // Сосед просит отдать лидерство. Отдаём штатным путём: флаг гасит эффект,
  // его cleanup останавливает контроллер и лишь затем отпускает лок.
  useEffect(() => onYieldRequest(() => {
    if (heldRef.current) setYielded(true);
  }), []);

  useEffect(() => {
    if (!enabled || yielded) return undefined;
    const myTick = takeTick;
    const isTakeover = myTick > 0 && takingTickRef.current === myTick;
    const doneTaking = () => {
      if (takingTickRef.current === myTick) takingTickRef.current = 0;
      setTakingTick((cur) => (cur === myTick ? 0 : cur));
    };
    let leader = null;
    let leaderP = null;
    let controller = null;
    let cancelled = false;
    (async () => {
      await waitLocalHandoff();
      if (cancelled) return;
      leaderP = requestLeadership(myTick > 0 ? { waitMs: TAKEOVER_WAIT_MS } : {});
      leader = await leaderP;
      doneTaking();
      if (cancelled) { leader.release(); return; }
      if (!leader.granted) { setBlocked(true); setTakeFailed(isTakeover); return; }
      heldRef.current = true;
      controller = startController();
      controllerRef.current = controller;
    })();
    return () => {
      cancelled = true;
      controllerRef.current = null;
      heldRef.current = false;
      const c = controller;
      const l = leader;
      const lp = leaderP;
      if (c) {
        setLocalHandoff(Promise.resolve(c.stop())
          .then(() => l?.release()).catch(() => undefined));
        return;
      }
      if (lp) setLocalHandoff(lp.then((got) => got.release()).catch(() => undefined));
      else l?.release();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, yielded, takeTick, retryTick, ...deps]);

  /**
   * «Открыть здесь» — единственный выход на телефоне, где вкладку-держателя не
   * найти и не закрыть. 🔴 Повторный тап во время переноса игнорируем: иначе
   * два подряд заводят два запроса лидерства и лишний перезапуск эффекта.
   */
  const takeover = useCallback(() => {
    if (takingTickRef.current > 0) return;
    const next = takeTick + 1;
    askYield();
    takingTickRef.current = next;
    setTakingTick(next);
    setBlocked(false);
    setTakeFailed(false);
    setTakeTick(next);
  }, [takeTick]);

  /** Повторить подъём: сбрасывает blocked, иначе он переживёт удачный повтор. */
  const retryNow = useCallback(() => {
    setBlocked(false);
    setTakeFailed(false);
    setRetryTick((n) => n + 1);
  }, []);

  return {
    blocked, yielded, taking, takeFailed, takeover, retryNow, controllerRef,
  };
}
